/**
 * 本文に差し込むYouTube動画
 *
 * 目的: 「YouTubeのURLを貼る」から「公開ページに埋め込み枠を出す」までの変換をここに集める。
 *
 * 方針: 本文HTMLに保存するのは動画IDを持つプレースホルダだけで、<iframe> は保存しない。
 * この本文HTMLはフロントの3つのサニタイザとバックエンドの MemberPostHtml（PHPの手書き実装）
 * を通る。4か所すべてで iframe の src を正しく絞り込む実装を保ち続けるより、
 * そもそも iframe を通さないほうが破綻しにくい。
 * 利用者の入力が届くのは11文字のIDだけで、iframe の src は常にこちらが組み立てる。
 */

/** YouTubeの動画IDの形。11文字ちょうど */
const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;

/** YouTubeの動画IDとして使ってよい値か */
export function isYouTubeVideoId(value: string): boolean {
  return VIDEO_ID_PATTERN.test(value ?? '');
}

/** 動画IDとして通る候補だけ返す（通らなければ null） */
function pickVideoId(candidate: string | null | undefined): string | null {
  const value = (candidate ?? '').trim();
  return isYouTubeVideoId(value) ? value : null;
}

/**
 * YouTubeのURL（または裸のID）から動画IDを取り出す
 *
 * 引数: input — 貼り付けられた文字列
 * 戻り値: 動画ID。取り出せなければ null
 *
 * 受け付ける形:
 * - https://www.youtube.com/watch?v=ID （余分なクエリが付いていてもよい）
 * - https://youtu.be/ID
 * - https://www.youtube.com/embed/ID, /shorts/ID, /live/ID, /v/ID
 * - 裸の11文字ID
 *
 * YouTube 以外のホストを受けないのは、こちらが組み立てる埋め込み先が
 * YouTube でなくなるのを防ぐため。
 */
export function parseYouTubeVideoId(input: string): string | null {
  const value = (input ?? '').trim();
  if (value === '') return null;
  if (isYouTubeVideoId(value)) return value;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const segments = url.pathname.split('/').filter(Boolean);

  if (host === 'youtu.be') {
    return pickVideoId(segments[0]);
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    if (segments[0] === 'watch') return pickVideoId(url.searchParams.get('v'));
    if (['embed', 'shorts', 'live', 'v'].includes(segments[0] ?? '')) {
      return pickVideoId(segments[1]);
    }
    return null;
  }
  return null;
}

/**
 * 本文に保存するプレースホルダ1つ分
 *
 * 空要素なので、開始タグから終了タグまでを丸ごと拾う。
 */
const PLACEHOLDER_PATTERN = /<div\b[^>]*\bdata-youtube-id="([^"]*)"[^>]*>\s*<\/div>/gi;

/**
 * 動画IDから埋め込み枠のHTMLを組み立てる
 *
 * 引数: videoId — 検証済みの動画ID
 * 戻り値: 埋め込み枠のHTML
 *
 * 仕様:
 * - youtube-nocookie.com を使う（再生前のCookie設置を避ける）
 * - loading="lazy" で、画面外の動画は読み込まない（記事が重くならない）
 * - allow に autoplay を含めない（開いただけで音が出るのを防ぐ）
 * - aspect-video で16:9の高さを確保する。読み込み前後で本文が飛び跳ねない
 *
 * 会員紹介ページの「さなえが行く」枠もこの関数を使う。埋め込みの見た目を2か所に
 * 書くと、片方だけ直されてズレる
 */
export function youTubeEmbedHtml(videoId: string): string {
  return (
    '<div class="my-6 aspect-video w-full overflow-hidden border border-line bg-black">' +
    `<iframe class="h-full w-full" src="https://www.youtube-nocookie.com/embed/${videoId}"` +
    ' title="YouTube動画" loading="lazy" allowfullscreen' +
    ' referrerpolicy="strict-origin-when-cross-origin"' +
    ' allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share">' +
    '</iframe></div>'
  );
}

/**
 * サニタイズ済みHTMLのプレースホルダを、埋め込み枠のHTMLに差し替える
 *
 * 引数: html — サニタイズ済みの本文HTML
 * 戻り値: 埋め込み枠に差し替えたHTML
 *
 * 呼ぶ順番が重要: 必ずサニタイズの「後」に呼ぶ。先に呼ぶと、組み立てた iframe を
 * サニタイザが消してしまう。順番は renderBodyHtml に閉じ込めてある
 */
export function renderYouTubeEmbeds(html: string): string {
  return (html ?? '').replace(PLACEHOLDER_PATTERN, (_match, rawId: string) =>
    isYouTubeVideoId(rawId) ? youTubeEmbedHtml(rawId) : '',
  );
}
